import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import fetcher from '../utils/fetcher';
import useSWR from 'swr';
import axios from "axios";
import ProductSkeleton from "../components/ProductSkeleton";
import ProductCard from "../components/ProductCard";
import Pagination from "../components/Pagination";
import DesignYourOwnCta from "../components/DesignYourOwnCta";
import { MdOutlineFilterAltOff } from "react-icons/md";
import { BsWhatsapp } from 'react-icons/bs';
import { FiGrid, FiDroplet, FiSearch, FiImage, FiLayers, FiBookOpen } from "react-icons/fi";
import styles from "../styles/products.module.css";

const Products = () => {
  const [searchParams] = useSearchParams();
  const [category, setCategory] = useState(() => {
    const categoryParam = searchParams.get('category');
    return categoryParam ? Number(categoryParam) : "";
  });
  const [color, setColor] = useState("");
  // "" = all, "false" = single panel (لوحة واحدة), "true" = multi-panel (لوحات متعددة)
  const [multi, setMulti] = useState("");
  // "" = all, "true" = with a Quranic verse (مع آية), "false" = without (بدون آية)
  const [hasAyah, setHasAyah] = useState("");
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [result, setResult] = useState(false);
  const search = searchParams.getAll('search');
  const [isVisible, setIsVisible] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 20;

  useEffect(() => {
    const handleScroll = () => {      
      // Check if near bottom (hide WhatsApp button)
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Hide when within 300px of bottom (adjust as needed)
      const isNearBottom = (scrollTop + windowHeight) >= (documentHeight - 200);
      setIsVisible(!isNearBottom);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const queryString = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.set('page', currentPage);
    params.set('limit', productsPerPage);
    if (category) params.set('category', category); else params.delete('category');
    if (color) params.set('color', color); else params.delete('color');
    if (multi) params.set('multi', multi); else params.delete('multi');
    if (hasAyah) params.set('hasAyah', hasAyah); else params.delete('hasAyah');
    return params.toString();
  }, [searchParams, currentPage, productsPerPage, category, color, multi, hasAyah]);

  const { data, error, isValidating } = useSWR(
    `${process.env.REACT_APP_BACKEND_URL}/api/products?${queryString}`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: true,
      shouldRetryOnError: (err) => err.status !== 304,
      errorRetryCount: 2,
      dedupingInterval: 10000
    }
  );
  
  const isLoadingInitial = !data || isValidating;

  // Access the fetched data
  const products = data?.products || [];
  const totalProducts = data?.totalCount ? Number(data.totalCount) : 0;
  const totalPages = Math.ceil(totalProducts / productsPerPage);
  const lowestPrices = data?.lowestPrices || [];
  
  const categoryOptions = dynamicCategories.map(cat => ({ value: cat.id, label: cat.name }));

  console.log("dynamicCategories:", dynamicCategories);
  const colorOptions = [
    { value: 'ترابية', label: 'الوان ترابية' },
    { value: 'فاتحة', label: 'الوان فاتحة' },
    { value: 'داكنة', label: 'الوان داكنة' },
    { value: 'الوان', label: 'الوان' },
    { value: 'أسود', label: 'أسود' },
    { value: 'أبيض', label: 'أبيض' }
  ];

  const multiOptions = [
    { value: 'false', label: 'لوحة واحدة' },
    { value: 'true', label: 'لوحات متعددة' },
  ];

  const ayahOptions = [
    { value: 'true', label: 'مع آية' },
    { value: 'false', label: 'بدون آية' },
  ];

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#e1ae8b' : '#9B773D',
      fontWeight: state.isSelected ? 'bold' : 'normal'
    })
  };

  const resetFilters = () => {
    setCategory("");
    setColor("");
    setMulti("");
    setHasAyah("");
    setCurrentPage(1);
  };

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/categories`)
      .then(res => setDynamicCategories(res.data ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (search.length > 0) {
      setResult(true);
    } else {
      setResult(false);
    }
  }, [search.length]);

  if (error && error.status !== 304) {
    return <p className="warn">حدث خطأ اثناء تحميل اللوحات. حاول مرة اخرى</p>
  };

  return (
    <div className={styles.page}>
      <a href="https://wa.me/972543255575" target="_blank" rel="noreferrer">
        <BsWhatsapp className={`whatsapp ${isVisible ? 'showWhats' : 'hideWhats'}`} />
      </a>

      <div className={styles.hero}>
        <div className={styles.heroText}>
          <h4>جميع اللوحات</h4>
          <span className={styles.heroAccent} />
          <p>اكتشف مجموعتنا الكاملة من اللوحات الفنية المميزة</p>
        </div>
        {totalProducts > 0 && (
          <span className={styles.countBadge}>{totalProducts} لوحة</span>
        )}
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <FiGrid className={styles.filterIcon} />
          <Select
            options={categoryOptions}
            onChange={(newValue) => {
              setCategory(newValue.value);
              setCurrentPage(1);
            }}
            value={categoryOptions.find(opt => opt.value === category) || null}
            placeholder="الفئة"
            styles={customStyles}
            className={styles.selectWrap}
            classNamePrefix="react-select"
            isSearchable={false}
          />
        </div>

        <div className={styles.filterGroup}>
          <FiDroplet className={styles.filterIcon} />
          <Select
            options={colorOptions}
            onChange={(newValue) => {
              setColor(newValue.value);
              setCurrentPage(1);
            }}
            value={colorOptions.find(opt => opt.value === color) || null}
            placeholder="الالوان"
            styles={customStyles}
            className={styles.selectWrap}
            classNamePrefix="react-select"
            isSearchable={false}
          />
        </div>

        <div className={styles.filterGroup}>
          <FiLayers className={styles.filterIcon} />
          <Select
            options={multiOptions}
            onChange={(newValue) => {
              setMulti(newValue.value);
              setCurrentPage(1);
            }}
            value={multiOptions.find(opt => opt.value === multi) || null}
            placeholder="نوع اللوحة"
            styles={customStyles}
            className={styles.selectWrap}
            classNamePrefix="react-select"
            isSearchable={false}
          />
        </div>

        <div className={styles.filterGroup}>
          <FiBookOpen className={styles.filterIcon} />
          <Select
            options={ayahOptions}
            onChange={(newValue) => {
              setHasAyah(newValue.value);
              setCurrentPage(1);
            }}
            value={ayahOptions.find(opt => opt.value === hasAyah) || null}
            placeholder="الآية"
            styles={customStyles}
            className={styles.selectWrap}
            classNamePrefix="react-select"
            isSearchable={false}
          />
        </div>

        <button className={styles.resetBtn} onClick={resetFilters}>
          <MdOutlineFilterAltOff className={styles.resetIcon} /> مسح الفلاتر
        </button>
      </div>

      {result && (
        <div className={styles.resultsBanner}>
          <FiSearch className={styles.resultsIcon} />
          <p>نتائج البحث عن "<strong>{search.join(' ')}</strong>"</p>
        </div>
      )}

      {isLoadingInitial ? (
        <ProductSkeleton count={6} />
      ) : (
        <>
          {products.length > 0 ? (
            <>
              <ProductCard products={products} lowestPrices={lowestPrices} />

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  disabled={isLoadingInitial}
                />
              )}
            </>
          ) : (
            <div className={styles.emptyState}>
              <span className={styles.emptyIconWrap}>
                <FiImage className={styles.emptyIcon} />
              </span>
              <h5>لا توجد لوحة بهذه المواصفات حالياً</h5>
              <p>جرّب تغيير الفلاتر أو مسحها للعثور على المزيد من اللوحات</p>
              <button className={styles.resetBtn} onClick={resetFilters}>
                <MdOutlineFilterAltOff className={styles.resetIcon} /> مسح الفلاتر
              </button>
            </div>
          )}

          {/* "لم تجد لوحتك بعد؟" — custom design call to action */}
          <DesignYourOwnCta />
        </>
      )}
    </div>
  );
};

export default Products;
